import { OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { storage } from "@gtmhub/core/storage";
import { TimeoutCache } from "@webapp/shared/utils/timeout-cache";

export type CacheableFormGroup<TControl extends { [K in keyof TControl]: AbstractControl<unknown> } = unknown> = FormGroup<TControl> & {
  cacheKey?: string;
  loadFromCache?(): void;
};

const cache = new Map<string, TimeoutCache<unknown>>();

const patchLifeCycleHook = <H extends keyof (OnInit & OnDestroy)>(target: object, hookName: H, hookFn: () => void): void => {
  const origHookFn = target[String(hookName)];
  if (!origHookFn) {
    target[String(hookName)] = hookFn;
  } else {
    target[String(hookName)] = function (): void {
      hookFn.apply(this);
      origHookFn.apply(this);
    };
  }
};

function attachLoadFromCache(this: object, propertyKey: string): void {
  const formGroup: CacheableFormGroup = this[propertyKey];
  formGroup.loadFromCache = function (): void {
    if (!this.cacheKey) {
      console.warn(`You have used @${CacheFormOnAccidentalModalClose.name} but you have not defined a cacheKey getter on the form group.`);
    } else if (cache.has(this.cacheKey)) {
      cache
        .get(this.cacheKey)
        .get$()
        .subscribe((value) => {
          if (value) {
            this.patchValue(value);
          }
        });
    }
  };
}

function updateCacheData(this: object, propertyKey: string, options: { timeout: number }): void {
  const formGroup: CacheableFormGroup = this[propertyKey];
  if (!formGroup.cacheKey) {
    console.warn(`You have used @${CacheFormOnAccidentalModalClose.name} but you have not defined a cacheKey getter on the form group.`);
    return;
  }

  const modalCloseReason = storage.get<string>("modalCloseReason");
  if (modalCloseReason && modalCloseReason.includes("backdrop")) {
    if (!cache.has(formGroup.cacheKey)) {
      cache.set(formGroup.cacheKey, new TimeoutCache(options));
    }

    cache.get(formGroup.cacheKey).set(formGroup.value);
  } else {
    cache.delete(formGroup.cacheKey);
  }
}

export function CacheFormOnAccidentalModalClose(options: { timeout: number }) {
  return function (target: object, propertyKey: string): void {
    const ngOnInit = function ngOnInit(this: object): void {
      attachLoadFromCache.apply(this, [propertyKey]);
    };
    patchLifeCycleHook(target, "ngOnInit", ngOnInit);

    const ngOnDestroy = function ngOnDestroy(this: object): void {
      updateCacheData.apply(this, [propertyKey, options]);
    };
    patchLifeCycleHook(target, "ngOnDestroy", ngOnDestroy);
  };
}
